/**
 * Feature Flags Hook
 * Fetches server feature flags once and caches for the session.
 * Returns DEFAULT_FEATURE_FLAGS (all false) until loaded — safe fallback.
 *
 * Also exposes `isLoading` so callers that make destructive decisions
 * (tab visibility, redirects) can wait until the server has responded
 * instead of acting on the default-false fallback. Without this, cold
 * page loads race with the /api/config/features fetch and redirect users
 * away from leaderboard-gated tabs before the real flags arrive.
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import { DEFAULT_FEATURE_FLAGS, type FeatureFlags } from '@parlay-picker/shared/featureFlags';
import { useEffect, useState } from 'react';

// Module-level cache — fetched once per page load
let cachedFlags: FeatureFlags | null = null;
let fetchPromise: Promise<FeatureFlags> | null = null;

async function fetchFlags(): Promise<FeatureFlags> {
  try {
    const res = await fetch(apiUrl(API_ENDPOINTS.CONFIG_FEATURES));
    if (!res.ok) return DEFAULT_FEATURE_FLAGS;
    const data = await res.json();
    return data.flags ?? DEFAULT_FEATURE_FLAGS;
  } catch {
    return DEFAULT_FEATURE_FLAGS;
  }
}

export interface UseFeatureFlagsResult extends FeatureFlags {
  /** True while the initial fetch is in flight (no cached flags yet). */
  isLoading: boolean;
}

export function useFeatureFlags(): UseFeatureFlagsResult {
  const [flags, setFlags] = useState<FeatureFlags>(cachedFlags ?? DEFAULT_FEATURE_FLAGS);
  const [isLoading, setIsLoading] = useState<boolean>(cachedFlags === null);

  useEffect(() => {
    if (cachedFlags) {
      setFlags(cachedFlags);
      setIsLoading(false);
      return;
    }

    if (!fetchPromise) {
      fetchPromise = fetchFlags();
    }

    fetchPromise.then((result) => {
      cachedFlags = result;
      setFlags(result);
      setIsLoading(false);
    });
  }, []);

  return { ...flags, isLoading };
}

/** Reset cache — useful for tests */
export function resetFeatureFlagsCache(): void {
  cachedFlags = null;
  fetchPromise = null;
}
